import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  Button,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Paper,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InsightsIcon from "@mui/icons-material/Insights";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";

// 役割別ガイドの定義。各権限ごとに主要なユースケースを網羅的に整理しています。
const roleDescriptions = [
  {
    role: "SYSTEM_ADMIN",
    label: "システム管理者",
    color: "primary",
    summary:
      "全体の設定・ユーザー管理・企業/ブランド/店舗の作成と監査を行います。最上位の権限です。",
    sections: [
      {
        title: "主な操作",
        items: [
          "企業（運営会社）を新規登録し、契約プランやステータスを更新する",
          "ブランドや店舗の登録状況を確認し、必要に応じて編集や削除を行う",
          "全ユーザーの一覧を確認し、ログインIDや権限の変更、アカウント無効化を行う",
          "システム設定（共通の環境変数や認証設定）を調整し、障害時の復旧を指揮",
          "売上や利用状況のレポートをダウンロードし、経営層にレポーティングする",
          "監査ログ・操作履歴を確認し、内部統制やセキュリティインシデントをレビューする",
        ],
      },
      {
        title: "典型的なユースケース",
        items: [
          "新規チェーン導入時に企業・ブランド・店舗をセットアップし、初期データを一括登録する",
          "四半期ごとのブランド統廃合に合わせて、関連するユーザーと店舗の紐付けを更新する",
          "外部システム（会計 SaaS、在庫管理）との API キーを設定し、データ連携を有効化する",
          "セキュリティポリシー変更に合わせてパスワードポリシーや多要素認証設定を強化する",
        ],
      },
      {
        title: "画面への行き方",
        items: [
          "左側メニュー『企業管理』から企業一覧や詳細の編集画面へ",
          "『ブランド管理』で企業配下のブランドを確認",
          "『店舗管理』で全店舗の営業時間・管理者情報を管理",
          "『ユーザー管理』でログインID・権限をメンテナンス",
          "『レポート』で期間を指定してCSVをエクスポート",
          "『設定』>『システム設定』で通知や統合設定を更新",
        ],
      },
    ],
  },
  {
    role: "COMPANY_ADMIN",
    label: "運営会社管理者",
    color: "success",
    summary:
      "自社に紐づくブランドやスタッフを管理します。複数店舗/ブランドを束ねる立場です。",
    sections: [
      {
        title: "主な操作",
        items: [
          "自社が運営するブランドを登録・編集し、ブランド管理者を任命",
          "各ブランドの数字（売上・来店数など）をダッシュボードで確認",
          "スタッフ向けアカウントの作成や権限変更（ブランド管理者など）",
          "レポート機能でブランド別・期間別の数字を確認し、改善指示に活用",
          "ブランド共通のキャンペーンルールやポイント施策を設定し、店舗へ配信",
          "ブランド横断の在庫アラートを確認し、在庫再配分を指示",
        ],
      },
      {
        title: "典型的なユースケース",
        items: [
          "新規ブランド立ち上げ時にブランド基本情報・ロゴを登録し、店舗管理者に引き継ぐ",
          "季節メニュー施策をブランド全体に展開し、店舗での反映状況をレポートで確認する",
          "ブランド別売上が目標未達の際に、ダッシュボードの KPI から改善対象店舗を抽出する",
          "ブランド管理者の交代に伴い、ロールと権限紐付けを更新し監査ログを確認する",
        ],
      },
      {
        title: "画面への行き方",
        items: [
          "サイドメニュー『ダッシュボード』で全体の概況を確認",
          "『ブランド管理』からブランドを新規作成・編集",
          "『スタッフ管理』でブランド管理者や店舗管理者を設定",
          "『レポート』でブランド別の主要指標をモニタリング",
        ],
      },
    ],
  },
  {
    role: "BRAND_ADMIN",
    label: "ブランド管理者",
    color: "warning",
    summary:
      "担当ブランドに属する店舗をまとめて管理します。店舗開店準備やブランド共通施策を担当。",
    sections: [
      {
        title: "主な操作",
        items: [
          "ブランド配下の店舗を新規作成し、店舗管理者を割り当てる",
          "各店舗の売上・注文件数・在庫状況を確認し、基準に満たない店舗へフォロー",
          "シーズンごとのメニュー更新や販促施策を店舗へ展開",
          "店舗横断のレポートを確認し、成功事例を共有",
          "ブランド固有のクーポンやディスカウント設定を管理し、反響を測定",
        ],
      },
      {
        title: "典型的なユースケース",
        items: [
          "新店舗オープン前にメニュー・スタッフ・POS 設定を一括で複製し、テスト注文を行う",
          "売上が低下している店舗の KPI を比較し、在庫や客単価の課題を抽出する",
          "キャンペーン期間中の割引設定を一時的に適用し、終了後に自動で解除されるよう予約する",
          "原価率が高い商品の在庫を把握し、店舗に廃棄ロス削減施策を共有する",
        ],
      },
      {
        title: "画面への行き方",
        items: [
          "『ダッシュボード』でブランド全体の動きを把握",
          "『店舗管理』で店舗の追加・編集と担当者設定",
          "『レポート』で期間別の売上比較",
          "『設定』でブランド情報（ロゴ・説明文など）を更新",
        ],
      },
    ],
  },
  {
    role: "STORE_ADMIN",
    label: "店舗管理者",
    color: "info",
    summary:
      "店舗の日々の営業をリードします。メニュー・スタッフ・売上管理をすべて担当。",
    sections: [
      {
        title: "主な操作",
        items: [
          "メニュー管理で商品を追加・価格変更・売り切れ設定",
          "スタッフ管理でPOSで働くスタッフのアカウント作成",
          "QRコード管理でテーブルやテイクアウト用の注文QRを発行",
          "売上管理で日次売上を確認し、レポートをダウンロード",
          "POS画面を使い、営業時間の開始・終了（開店/閉店ボタン）を操作",
          "在庫残数の更新、仕入先への発注数確定、欠品アラートの対応",
          "店舗別キャンペーン（ハッピーアワー等）のスケジュール設定",
        ],
      },
      {
        title: "典型的なユースケース",
        items: [
          "開店前チェックリストに沿って POS の接続確認、レシートプリンター/キャッシュドロワーをテスト",
          "急な価格変更依頼に対応し、メニュー反映後にスタッフへプッシュ通知で共有",
          "売上集計が会計システムと突合しない場合に、訂正伝票や返品を確認し差異を解消",
          "閉店後に日次レポートをダウンロードし、原価率や客単価を本部へ報告する",
        ],
      },
      {
        title: "画面への行き方",
        items: [
          "左メニュー『ダッシュボード』で当日の売上や人気商品を確認",
          "『メニュー管理』で商品をカテゴリに分けて整備",
          "『スタッフ管理』で従業員のログインIDとパスワードを配布",
          "『設定』で店舗の営業時間や連絡先を更新",
          "POS画面（/pos/:storeId）からタブレットでリアルタイム注文管理",
        ],
      },
    ],
  },
  {
    role: "STORE_STAFF",
    label: "店舗スタッフ",
    color: "default",
    summary:
      "レジ・ホールなど現場で注文を扱います。POS画面から注文入力・会計・履歴確認を行います。",
    sections: [
      {
        title: "主な操作",
        items: [
          "POSの注文入力画面でお客様の注文を登録",
          "会計画面でお支払い対応、レシートの確認",
          "注文履歴から過去の注文を呼び出し、再会計や訂正を行う",
          "店長から共有されたメニュー更新に合わせて商品を案内",
          "テーブル移動・注文分割・割引適用などの細かな操作を実施",
          "端末の接続トラブル時にオフラインモードへ切り替え、復旧後に同期",
        ],
      },
      {
        title: "画面への行き方",
        items: [
          "ログイン後、自動で POS 画面へ遷移",
          "上部ボタン『注文入力』『会計』『注文履歴』で表示を切り替え",
          "営業時間中は店長に営業開始（開店）ボタンを押してもらうよう確認",
        ],
      },
    ],
  },
];

// 業務フロー別ユースケースの定義。システム全体で想定される操作シナリオを網羅しています。
const useCaseSections = [
  {
    title: "初期導入と権限セットアップ",
    summary:
      "契約締結後にチェーン全体を立ち上げる際の一連の流れです。権限設計やデータ移行を含みます。",
    steps: [
      {
        name: "契約情報と企業プロファイル登録",
        owner: "SYSTEM_ADMIN",
        detail:
          "企業名称・契約プラン・請求先情報を登録し、サブドメインやブランドドメインを確定します。",
      },
      {
        name: "ブランド・店舗の初期一括登録",
        owner: "SYSTEM_ADMIN / COMPANY_ADMIN",
        detail:
          "CSV テンプレートからブランド/店舗情報を一括インポートし、営業時間や所在地を設定します。",
      },
      {
        name: "ユーザー作成と権限割当",
        owner: "SYSTEM_ADMIN",
        detail:
          "各組織階層の責任者をロールごとに登録し、多要素認証や初回パスワードを設定します。",
      },
      {
        name: "メニューと価格データ移行",
        owner: "BRAND_ADMIN",
        detail:
          "既存のメニュー表からカテゴリ・商品・オプション・税率設定を取り込み、写真や説明文も整備します。",
      },
      {
        name: "テスト注文と検収",
        owner: "STORE_ADMIN / STORE_STAFF",
        detail:
          "POS 端末でテスト注文・取消・会計を実施し、レシート出力とバックエンドの売上反映を確認します。",
      },
    ],
  },
  {
    title: "日次オペレーション",
    summary:
      "開店前から閉店後までの毎日のルーティン。注文、会計、スタッフ配置、レポート確認を含みます。",
    steps: [
      {
        name: "開店準備（開錠・在庫チェック・端末接続）",
        owner: "STORE_ADMIN",
        detail:
          "開店ボタンを押下し、在庫残数を確認。レシートプリンターや決済端末の接続状況を点検します。",
      },
      {
        name: "注文受付（イートイン／テイクアウト／QR 注文）",
        owner: "STORE_STAFF",
        detail:
          "テーブル番号や注文タイプを選択し、商品追加・数量変更・メモ入力を行ったうえで確定します。",
      },
      {
        name: "会計・決済処理",
        owner: "STORE_STAFF",
        detail:
          "現金・クレジット・交通系 IC・QR 決済・外部プリペイドに対応。分割会計や割引・クーポン適用も可能です。",
      },
      {
        name: "シフト・スタッフ管理",
        owner: "STORE_ADMIN",
        detail:
          "出退勤記録を確認し、スタッフ権限やテンポラリパスワードを更新。欠勤時には代替スタッフを手配します。",
      },
      {
        name: "閉店処理（締め作業・日次報告）",
        owner: "STORE_ADMIN",
        detail:
          "売上締め報告を出力し、現金残高との突合、返品・値引きの承認ログをレビュー。翌日の在庫発注を登録します。",
      },
    ],
  },
  {
    title: "POS 会計・決済バリエーション",
    summary:
      "POS 画面でサポートされる決済フローの網羅リストです。イレギュラー対応も含みます。",
    steps: [
      {
        name: "通常会計",
        owner: "STORE_STAFF",
        detail:
          "税率自動計算、端末連携決済、レシート印刷を行います。現金会計では釣銭計算を自動表示。",
      },
      {
        name: "分割・合算会計",
        owner: "STORE_STAFF",
        detail:
          "テーブルごとの注文を分割、複数テーブルの会計を合算。催事など特別会場でも利用可能です。",
      },
      {
        name: "返品・訂正・返金",
        owner: "STORE_ADMIN",
        detail:
          "返品理由を選択し、レシート番号との紐付けを必須化。キャッシュレス返金は決済端末と連動して処理されます。",
      },
      {
        name: "クーポン・割引・ポイント",
        owner: "BRAND_ADMIN / STORE_STAFF",
        detail:
          "ブランド共通クーポン、店舗限定キャンペーン、スタッフ割引、ポイント即時利用が可能。重複適用のルールも設定済みです。",
      },
      {
        name: "自動注文・セルフオーダー連携",
        owner: "STORE_ADMIN",
        detail:
          "テーブル QR、キッチンディスプレイシステム、デリバリーアプリからの注文を自動受信し、POS にキュー表示します。",
      },
    ],
  },
  {
    title: "在庫・仕入・原価管理",
    summary:
      "商品ごとの在庫、廃棄、仕入先発注に関するシナリオです。原価管理とロス対策を含みます。",
    steps: [
      {
        name: "在庫カウントと調整",
        owner: "STORE_ADMIN",
        detail:
          "棚卸モードで実棚数を入力し、POS 在庫との差異を理由付きで調整。差異は本部にアラート送信されます。",
      },
      {
        name: "仕入先への自動発注",
        owner: "STORE_ADMIN / BRAND_ADMIN",
        detail:
          "最小在庫数を下回った商品を自動でピックアップし、テンプレートメールまたは EDI で発注を送信します。",
      },
      {
        name: "廃棄・ロス記録",
        owner: "STORE_STAFF",
        detail:
          "廃棄理由・数量・担当者を記録し、原価率レポートへ自動連携。アレルゲン管理にも反映されます。",
      },
      {
        name: "原価率分析",
        owner: "COMPANY_ADMIN",
        detail:
          "ブランド別・店舗別・商品別の原価率を集計し、値付け見直しや仕入ロットの最適化につなげます。",
      },
    ],
  },
  {
    title: "レポート・分析・外部連携",
    summary: "経営判断や会計処理に必要なデータ活用シナリオです。",
    steps: [
      {
        name: "日次・週次・月次レポート出力",
        owner: "SYSTEM_ADMIN / COMPANY_ADMIN",
        detail:
          "売上・客数・客単価・原価率・返品などを期間指定でエクスポート。BI ツール連携用 API も提供。",
      },
      {
        name: "会計ソフト連携",
        owner: "SYSTEM_ADMIN",
        detail:
          "freee / マネーフォワードなど会計ソフト向けの仕訳 CSV を出力し、自動取り込みをスケジュール設定します。",
      },
      {
        name: "KPI ダッシュボード",
        owner: "BRAND_ADMIN",
        detail:
          "ビジュアルダッシュボードで、時間帯別売上、トップ商品、稼働スタッフをリアルタイム確認。異常値は通知します。",
      },
      {
        name: "アラート・通知設定",
        owner: "SYSTEM_ADMIN",
        detail:
          "売上急落、在庫切れ、端末オフライン検知などのシグナルをメール・Slack・Teams へ送信する設定を行います。",
      },
    ],
  },
  {
    title: "サポート・トラブルシューティング",
    summary: "障害発生時や問い合わせ対応の標準フローです。",
    steps: [
      {
        name: "ログインできない／アカウントロック",
        owner: "SYSTEM_ADMIN / STORE_ADMIN",
        detail:
          "ユーザー一覧からロック解除、パスワードリセットを実施。二段階認証失敗時のバックアップコード発行も可能です。",
      },
      {
        name: "端末・周辺機器トラブル",
        owner: "STORE_ADMIN",
        detail:
          "POS 端末の再起動、プリンター接続確認、オフラインモードへの切替を行い、復旧後に注文データを同期します。",
      },
      {
        name: "売上データ差異調査",
        owner: "SYSTEM_ADMIN",
        detail:
          "操作履歴・監査ログ・API ログを参照し、手動調整・返金・データ同期遅延の有無を確認します。",
      },
      {
        name: "サポート窓口へのエスカレーション",
        owner: "全ロール",
        detail:
          "ヘルプページ記載のメール/チャット窓口へスクリーンショット・ログ添付で連絡。重大障害は専用ホットラインへ。",
      },
    ],
  },
];

// FAQ の定義。問い合わせが多い順に整理し、参照すべき画面も明記します。
const faqItems = [
  {
    question: "ログインできません。",
    answer:
      "入力した『ログインID』『パスワード』に誤字がないか確認してください。5回以上失敗するとロックされます。店舗管理者またはシステム管理者にロック解除・パスワードリセットを依頼してください。",
    related: "ユーザー管理 > アカウント編集",
  },
  {
    question: "メニューを追加したのに POS に表示されません。",
    answer:
      "メニュー保存後、対象店舗にメニューが紐付いているか確認します。カテゴリ非表示、在庫ゼロ、公開日時の未来設定が原因の場合があります。POS 画面を再読み込みすると最新メニューが表示されます。",
    related: "メニュー管理 > メニュー詳細",
  },
  {
    question: "会計ボタンが押せません。",
    answer:
      "店舗管理者が営業開始（開店）しているか、決済端末がオンラインかを確認してください。決済端末のペアリングが解除されている場合は再接続が必要です。",
    related: "POS 画面 > 会計",
  },
  {
    question: "返品・返金はどう処理しますか？",
    answer:
      "注文履歴から対象伝票を選び、返品理由と数量を指定します。キャッシュレス決済の場合は同じ手段で自動返金され、レシートに返金記録が追加されます。",
    related: "POS 画面 > 注文履歴",
  },
  {
    question: "外部システムとデータ連携したい。",
    answer:
      "システム管理者は『設定 > システム連携』から API キーを発行できます。Webhook の URL 登録やスケジュールエクスポートもここで設定します。",
    related: "設定 > システム連携",
  },
];

// サポート連絡先と参考リンク集。緊急度に応じた問い合わせ先を案内します。
const supportResources = {
  contacts: [
    "通常問い合わせ: support@example-pos.com (平日 10:00-18:00)",
    "緊急障害ホットライン: 050-1234-5678 (24時間)",
    "チャットサポート: 管理画面右下のサポートウィジェット",
  ],
  documents: ["導入マニュアル（PDF）", "API リファレンス", "障害時対応フロー"],
  tips: [
    "重大な設定変更の前にはサンドボックス環境で検証しましょう。",
    "週次で売上レポートを自動メール配信することで担当者の確認漏れを防げます。",
    "オフラインモードでも会計可能ですが、復旧後に同期が完了しているかダッシュボードで確認してください。",
  ],
};

const HelpPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = React.useState(0);
  const [searchTerm, setSearchTerm] = React.useState("");

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const filteredFaqItems = faqItems.filter((item) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      item.question.toLowerCase().includes(term) ||
      item.answer.toLowerCase().includes(term) ||
      item.related.toLowerCase().includes(term)
    );
  });

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>
      <Typography variant="h3" gutterBottom>
        はじめてでも分かる！POS システムの使い方
      </Typography>
      <Typography variant="body1" paragraph>
        このヘルプページでは、あなたの担当する役割ごとに「どこを触ればいいのか」「何ができるのか」をやさしく解説します。
        ログイン後に迷子にならないよう、メニュー構成や操作の流れを順番に確認していきましょう。
      </Typography>

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 3 },
          mt: 3,
          mb: 4,
          boxShadow: (theme) => theme.shadows[4],
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="h5" gutterBottom>
              すばやく探す
            </Typography>
            <Typography variant="body2" color="text.secondary">
              項目を検索するか、右側のボタンから目的のセクションへ移動できます。
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="キーワードで検索 (例: メニュー 返品)"
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction={isMobile ? "column" : "row"} spacing={1}>
              <Button
                variant="contained"
                color="primary"
                endIcon={<ArrowForwardIcon />}
                href="#roles"
                fullWidth={isMobile}
              >
                役割ガイド
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                endIcon={<ArrowForwardIcon />}
                href="#workflows"
                fullWidth={isMobile}
              >
                業務フロー
              </Button>
              <Button
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
                href="#support"
                fullWidth={isMobile}
              >
                緊急連絡先
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Grid
        container
        spacing={2}
        sx={{
          my: 2,
          p: { xs: 2, md: 3 },
          backgroundColor: "background.paper",
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
        id="quick-actions"
      >
        <Grid item xs={12} md={4}>
          <Stack direction="row" spacing={2} alignItems="center">
            <WarningAmberIcon color="error" fontSize="large" />
            <Box>
              <Typography variant="subtitle1">緊急時の対応フロー</Typography>
              <Typography variant="body2" color="text.secondary">
                障害が発生したら、最初に「サポート・トラブルシューティング」セクションをご確認ください。
              </Typography>
              <Button size="small" href="#support" sx={{ mt: 1 }}>
                緊急連絡先へ移動
              </Button>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack direction="row" spacing={2} alignItems="center">
            <HelpOutlineIcon color="primary" fontSize="large" />
            <Box>
              <Typography variant="subtitle1">よくある質問を検索</Typography>
              <Typography variant="body2" color="text.secondary">
                下の検索ボックスでキーワード検索ができます。まずは FAQ
                を確認しましょう。
              </Typography>
              <Button size="small" href="#faq" sx={{ mt: 1 }}>
                FAQセクションへ
              </Button>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack direction="row" spacing={2} alignItems="center">
            <InsightsIcon color="secondary" fontSize="large" />
            <Box>
              <Typography variant="subtitle1">データ活用ガイド</Typography>
              <Typography variant="body2" color="text.secondary">
                分析・外部連携のユースケースは「レポート・分析」タブでまとめています。
              </Typography>
              <Button size="small" href="#workflows" sx={{ mt: 1 }}>
                業務フローを見る
              </Button>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      <Card id="toc" sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            コンテンツ目次
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                役割別ガイド
              </Typography>
              <List dense>
                {roleDescriptions.map((role) => (
                  <ListItem key={role.role} sx={{ py: 0 }}>
                    <ListItemText
                      primary={role.label}
                      primaryTypographyProps={{
                        component: "a",
                        href: `#role-${role.role.toLowerCase()}`,
                        sx: { textDecoration: "none", color: "primary.main" },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                業務フロー
              </Typography>
              <List dense>
                {useCaseSections.map((section) => (
                  <ListItem key={section.title} sx={{ py: 0 }}>
                    <ListItemText
                      primary={section.title}
                      primaryTypographyProps={{
                        component: "a",
                        href: `#workflow-${section.title}`,
                        sx: { textDecoration: "none", color: "primary.main" },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                サポート情報
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText
                    primary="FAQ"
                    primaryTypographyProps={{
                      component: "a",
                      href: "#faq",
                      sx: { textDecoration: "none", color: "primary.main" },
                    }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText
                    primary="サポート連絡先"
                    primaryTypographyProps={{
                      component: "a",
                      href: "#support",
                      sx: { textDecoration: "none", color: "primary.main" },
                    }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText
                    primary="参考ドキュメント"
                    primaryTypographyProps={{
                      component: "a",
                      href: "#support-docs",
                      sx: { textDecoration: "none", color: "primary.main" },
                    }}
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box id="roles">
        <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
          役割別ガイド
        </Typography>
        <Stack spacing={2}>
          {roleDescriptions.map((role) => (
            <Accordion
              key={role.role}
              id={`role-${role.role.toLowerCase()}`}
              defaultExpanded
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ width: "100%", justifyContent: "space-between" }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={role.label} color={role.color} />
                    <Typography variant="subtitle2" color="text.secondary">
                      権限コード: {role.role}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {role.summary}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {role.sections.map((section, index) => (
                  <Box key={index} sx={{ mt: index === 0 ? 0 : 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      {section.title}
                    </Typography>
                    <List dense>
                      {section.items.map((item, idx) => (
                        <ListItem key={idx} sx={{ pl: 2 }}>
                          <ListItemText primary={`・${item}`} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box id="workflows">
        <Typography variant="h5" gutterBottom>
          業務フロー別ユースケース
        </Typography>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="業務フロータブ"
          sx={{ mb: 2 }}
        >
          {useCaseSections.map((section, index) => (
            <Tab
              key={section.title}
              label={section.title}
              value={index}
              id={`workflow-tab-${index}`}
              aria-controls={`workflow-tabpanel-${index}`}
            />
          ))}
        </Tabs>
        {useCaseSections.map((section, index) => (
          <Box
            key={section.title}
            role="tabpanel"
            hidden={activeTab !== index}
            id={`workflow-tabpanel-${index}`}
            aria-labelledby={`workflow-tab-${index}`}
            sx={{ mt: 2 }}
          >
            {activeTab === index && (
              <Card id={`workflow-${section.title}`}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {section.title}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {section.summary}
                  </Typography>
                  <List dense>
                    {section.steps.map((step) => (
                      <ListItem
                        key={step.name}
                        alignItems="flex-start"
                        sx={{ flexDirection: "column", alignItems: "stretch" }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold" }}
                        >
                          {step.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          担当: {step.owner}
                        </Typography>
                        <ListItemText primary={step.detail} sx={{ mt: 0.5 }} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h5" gutterBottom>
        よくある質問（FAQ）
      </Typography>
      <Box sx={{ mt: 2 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              よくある質問一覧
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              {filteredFaqItems.map((item) => (
                <ListItem
                  key={item.question}
                  sx={{ flexDirection: "column", alignItems: "flex-start" }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    Q. {item.question}
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ width: "100%" }}>
                    {item.answer}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    関連画面: {item.related}
                  </Typography>
                </ListItem>
              ))}
              {filteredFaqItems.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2 }}
                >
                  該当する FAQ
                  は見つかりませんでした。キーワードを変更して再検索してください。
                </Typography>
              )}
            </List>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          わからないことは？
        </Typography>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              サポート連絡先
            </Typography>
            <List dense>
              {supportResources.contacts.map((contact) => (
                <ListItem key={contact} sx={{ pl: 2 }}>
                  <ListItemText primary={`・${contact}`} />
                </ListItem>
              ))}
            </List>
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              参考ドキュメント
            </Typography>
            <List dense>
              {supportResources.documents.map((doc) => (
                <ListItem key={doc} sx={{ pl: 2 }}>
                  <ListItemText primary={`・${doc}`} />
                </ListItem>
              ))}
            </List>
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              運用のヒント
            </Typography>
            <List dense>
              {supportResources.tips.map((tip) => (
                <ListItem key={tip} sx={{ pl: 2 }}>
                  <ListItemText primary={`・${tip}`} />
                </ListItem>
              ))}
            </List>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default HelpPage;
